<template>
  <div>
    <teleport to="#pushed-to-rag">
      <n-search-container
        v-if="!isChatRoom"
        :fields="searchFields"
        @search="search"
        @reset="reset"
      >
        <n-search-input
          ref="questSearch"
          v-model="params"
          :fields="searchFields"
        >
          <template #startDate="{ field }">
            <div v-if="params.period != 'other'" />
          </template>
          <template #endDate>
            <div v-if="params.period != 'other'" />
          </template>
        </n-search-input>
        <span
          v-if="params.period === 'other' && isDateRangeOverOneMonth"
          class="text-danger"
        >
          {{ $t('invalid_selected_date_range') }}
        </span>
      </n-search-container>
    </teleport>
    <load-profile />
    <b-card
      v-show="!isChatRoom"
      no-body
      class="mb-0"
    >
      <div class="p-2">
        <div class="d-flex lang-container">
          <div>
            <label>{{ $t("general.entries") }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-50 mr-1 w-20"
            >
              <template #no-options>
                {{ $t("selection.noOption") }}
              </template>
            </v-select>
          </div>

          <!-- <div v-if="$can('create', permissionType)" class="btn-filter-bot">
            <n-button-loading
              variant="primary"
              class="btn-submit-bot-filter"
              :loading="loadingDownload"
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              @submit="download"
              >
              {{ $t("button.download") }}
            </n-button-loading>
          </div> -->
        </div>
      </div>
      <div>
        <n-table
          ref="table"
          class="list-table"
          :fields="fields"
          :items="items"
          :busy="loading"
          :current-page="params.page"
          :per-page="$store.state.pagination.perPage"
          :total="total"
          :deletable="true"
          :type="permissionType"
          :route="route"
          :updatable="false"
          sticky-header="800px"
          @destroy="destroy"
        >
          <template #cell(topic)="data">
            <div
              style="display: flex; flex-direction: column; gap: 2px; white-space: nowrap;"
            >
              <div>
                <flag
                  iso="us"
                  :squared="false"
                />
                {{ data.item.topic.en }}
              </div>
              <div>
                <flag
                  iso="cn"
                  :squared="false"
                />
                {{ data.item.topic.zh }}
              </div>
            </div>
          </template>
          <template #cell(conversation)="data">
            <div
              class="d-block"
              style="width: 400px; white-space: pre-wrap; word-wrap: break-word; overflow-wrap: break-word;"
              v-html="handleTransform(data.item.conversation)"
            />
          </template>
          <template #cell(freqCount)="data">
            <b-link>
              {{ data.item.freqCount }}
            </b-link>
          </template>
          <template #cell(time)="data">
            {{ data.item.time | formatDate }}
          </template>
          <template #front-button="data">
            <b-button
              v-if="$can('update', permissionType)"
              v-b-tooltip.hover
              :title="$t('button.update')"
              variant="primary"
              pill
              size="sm"
              @click="showEditModal(data.item)"
            >
              <feather-icon
                icon="EditIcon"
              />
            </b-button>
          </template>
        </n-table>
        <n-pagination
          class="mt-2"
          :total="total"
          :per-page="$store.state.pagination.perPage"
          :page="query.page"
          @change="list"
        />
      </div>
    </b-card>
    <b-card
      v-show="isChatRoom"
      no-body
      class="mb-0"
    >
      <h4 class="mb-3">
        {{ selectedData.id }}
      </h4>
      <div>
        <n-table
          ref="table"
          :fields="channelTableField"
          :items="selectedData.result ||[]"
          :per-page="1000"
          :current-page="params.page"
          :total="total"
          :deletable="false"
          :type="permissionType"
          :route="route"
          :updatable="false"
        >
          <template #cell(chatChannelName)="data">
            <span v-if="data.item.channel">
              {{ data.item.channel.name }}
            </span>
          </template>
          <template #cell(topics)="data">
            <span
              v-for="(topic, index) in data.item.topics"
              :key="index"
              class="d-block mb-1"
            >
              {{ getTopicByCurrentLang(topic) }}
            </span>
          </template>
          <template #cell(conversationDate)="data">
            <span
              v-if="data.item.logDate"
              class="d-block mb-1"
            >
              {{ data.item.logDate | formatDate("LL") }}
            </span>
          </template>
          <template #front-button="data">
            <b-button
              v-b-tooltip.hover
              :title="$t('breadcrumb.chat')"
              variant="info"
              pill
              size="sm"
              @click="redirectToChatRoom(data.item)"
            >
              <feather-icon icon="MessageCircleIcon" />
            </b-button>
          </template>
        </n-table>
      </div>
      <b-row>
        <b-col
          cols="12"
          class="text-right"
        >
          <n-button-loading
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            type="button"
            variant="outline-secondary"
            class="mt-1"
            @submit="back"
          >
            {{ $t("button.back") }}
          </n-button-loading>
        </b-col>
      </b-row>
    </b-card>

    <conversation-summary-form-modal
      ref="formModal"
      is-updated
      @submit="getData"
    />
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BBadge,
  BLink,
  BSpinner,
  BButton,
  BForm,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Repository from '@/repositories/RepositoryFactory'
import NPagination from '@/components/NPagination'
import NTable from '@/components/NTable'
import { avatarText } from '@core/utils/filter'
import NInput from '@/components/NInput'
import NButtonLoading from '@/components/NButtonLoading'
import Ripple from 'vue-ripple-directive'
import NSearchInput from '@/components/NSearchInput'
import teleport from '@/helpers/teleport'
import NSearchContainer from '@/components/NSearchContainer.vue'
import { calculateDatesByPeriod } from '@/libs/helper'
import NFormConfirmation from '@/components/NFormConfirmation'
import * as XLSX from 'xlsx'
import NDatePicker from '@/components/NDatePicker'
import store from '@/store'
import channelTableField from './channelTableField'
import tableFields from './tableField'
import formInput from './updateQuestionFrom'
import SearchInputs from './searchInput'
import ConversationSummaryFormModal from '../conversation-summary/FormModal.vue'

const TopicSummaryRepository = Repository.get('topicSummary')

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BLink,
    BBadge,
    BSpinner,
    NSearchContainer,
    BButton,
    BForm,
    NPagination,
    NTable,
    vSelect,
    NInput,
    NButtonLoading,
    teleport,
    NSearchInput,
    NFormConfirmation,
    NDatePicker,
    ConversationSummaryFormModal,
  },
  directives: {
    Ripple,
  },
  props: {
    tabIndex: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      items: [],
      loading: false,
      resendLoading: false,
      total: 0,
      perPageOptions: this.$store.state.pagination.perPageOptions,
      params: {
        period: this.$route.query.period || 'thisWeek',
        startDate: this.$route.query.startDate || null,
        endDate: this.$route.query.endDate || null,
        topic: this.$route.query.topic || '',
      },
      query: {
        page: Number(this.$route.query.page) || 1,
        limit: Number(this.$route.query.limit) || 25,
      },
      avatarText,
      data: {
        question: '',
        answer: '',
      },
      options: [],
      listLanguage: [],
      initData: {},
      loadingDownload: false,
      selectedId: null,
      isEdit: false,
      selectedData: {},
      isChatRoom: false,
      formData: {
        conversation: '',
        // fullConversation: '',
      },
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.state.pagination.perPage
      },
      set(value) {
        this.$store.commit('pagination/SET_PER_PAGE', value)
      },
    },
    isDateRangeOverOneMonth() {
      if (this.params.startDate && this.params.endDate) {
        const start = new Date(this.params.startDate)
        const end = new Date(this.params.endDate)
        const oneMonthLater = new Date(start)
        oneMonthLater.setMonth(oneMonthLater.getMonth() + 1)
        return end >= oneMonthLater
      }
      return false
    },
  },
  watch: {
    perPage() {
      this.list(1)
    },
    'params.period': function (value) {
      this.calculateDate()
    },
    '$i18n.locale': function (value) {
      this.getData()
    },
    tabIndex: {
      immediate: true,
      handler(value) {
        if (value === 1) {
          const query = { ...store.state.app.pushedToRag }
          if (!query.period && !query.startDate && !query.endDate) {
            query.period = 'thisWeek'
          }

          this.query = query
          this.params = query
          this.updateQuerySting()
        }
      },
    },
  },

  mounted() {
    this.getData()
  },
  methods: {
    async onSubmit() {
      const isValid = await this.$refs.formValidation.validate()
      if (!isValid || this.submitting) return

      this.submitting = true
      try {
        await TopicSummaryRepository.updateConversationSummary({
          topics: [{
            conversation: this.formData.conversation,
          }],
          statusPushToRag: 'approved',
          // fullConversation: this.formData.fullConversation,
        })
        this.$toast.success(this.$t('message.update_success'))
        this.hideEditModal()
        this.getData()
      } catch (error) {
        console.error('Update failed:', error)
        this.$toast.error(this.$t('message.update_failed'))
      } finally {
        this.submitting = false
      }
    },
    hideEditModal() {
      this.$refs.editModal.hide()
    },
    updateItemInList(updatedItem) {
      // Update item in selectedData.result array
      const index = this.selectedData.result.findIndex(item => item.id === updatedItem.id)
      if (index !== -1) {
        // Update the item while maintaining reactivity
        this.$set(this.selectedData.result, index, {
          ...this.selectedData.result[index],
          topics: {
            ...this.selectedData.result[index].topics,
            conversation: updatedItem.topics[0].conversation,
          },
          // fullConversation: updatedItem.fullConversation,
        })
      }
    },
    showEditModal(item) {
      this.$refs.formModal.show({
        ...item,
        topics: {
          ...item.topic,
          conversation: item.conversation,
        },
      })
    },
    destroy(id) {
      TopicSummaryRepository.deleteKnowledgeRecord(id)
        .catch(err => {
          console.log('err', err)
        })
        .finally(() => {
          this.getData()
        })
    },
    handleTransform(text) {
      const conversation = text
        .split('\n')
        .map(line => line)
        .join('\n')
      return conversation
    },
    updateQuerySting() {
      this.$router.replace({ query: this.query })
      this.$nextTick(() => {
        if (this.query.cache != 'no') {
          store.commit('app/UPDATE_PUSHED_TO_RAG', { ...this.query, tabIndex: 1 })
        }
      })
    },
    list(page = 1) {
      this.query.page = page
      this.query.limit = this.perPage
      this.updateQuerySting()
      this.getData()
    },
    onChange(value) {
      if (value?.languageId) {
        this.query.lang = value?.languageId
        this.getData()
      }
    },
    getData() {
      if (this.isDateRangeOverOneMonth) {
        return
      }
      this.loading = true
      if (!this.params.startDate || !this.params.endDate) {
        this.calculateDate()
      } else {
        this.formatIsoTimeString()
      }
      TopicSummaryRepository.pushedToRageList({
        ...this.query,
        topic: this.params.topic ? this.params.topic.trim() : '',
        lang: this.$i18n.locale.split('-')[0] === 'zh' ? 'zh' : 'en',
      })
        .then(response => {
          const { list } = response.data.data

          this.items = [...list]
          this.total = response.data.data.total
          if (this.selectedData.result) {
            this.selectedData = this.items.filter(item => item.result[0].channel.id === this.selectedData.result[0].channel.id)[0]
          }
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false
        })
    },
    // destroy(id) {
    //   TopicSummaryRepository.destroyTopicSummary(id)
    //     .catch(err => {
    //       console.log('err', err)
    //     })
    //     .finally(() => {
    //       this.getData()
    //     })
    // },
    calculateDate() {
      let dates
      if (this.params.period == 'other') {
        dates = calculateDatesByPeriod('thisWeek')
      } else {
        dates = calculateDatesByPeriod(this.params.period)
      }
      if (dates.length) {
        // Set start date to 12:00:00 AM
        const startDate = new Date(dates[0])
        startDate.setHours(0, 0, 0, 0)

        // Set end date to 11:59:59 PM
        const endDate = new Date(dates[1])
        endDate.setHours(23, 59, 59, 999)

        this.params.startDate = startDate.toISOString()
        this.params.endDate = endDate.toISOString()
        this.query.startDate = startDate.toISOString()
        this.query.endDate = endDate.toISOString()
      }
    },
    formatIsoTimeString() {
      const startDate = new Date(this.params.startDate)
      startDate.setHours(0, 0, 0, 0)
      // Set end date to 11:59:59 PM
      const endDate = new Date(this.params.endDate)
      endDate.setHours(23, 59, 59, 999)
      this.query.startDate = startDate.toISOString()
      this.query.endDate = endDate.toISOString()
    },
    search() {
      if (this.isDateRangeOverOneMonth) {
        return
      }
      this.list()
    },
    reset() {
      this.$refs.questSearch.reset()
      this.params.period = 'thisWeek'
      this.calculateDate()
      this.updateQuerySting()
      this.getData()
    },
    validateForm() {
      this.$refs.editForm.validate().then(async success => {
        if (success) {
          await this.submit()
        }
      })
    },
    clickRoom(data) {
      this.selectedData = data
      this.isChatRoom = true
    },
    back() {
      this.isChatRoom = false
    },
    download() {
      this.loadingDownload = true
      if (!this.params.startDate || !this.params.endDate) {
        this.calculateDate()
      } else {
        this.formatIsoTimeString()
      }
      TopicSummaryRepository.downloadFrequentlyTopicSummary({
        startDate: this.params.startDate,
        endDate: this.params.endDate,
        lang: this.$i18n.locale.split('-')[0] === 'zh' ? 'zh' : 'en',
      })
        .then(async response => {
          let arrayBuffer
          // Determine the array buffer from the response
          if (response.data instanceof ArrayBuffer) {
            arrayBuffer = response.data
          } else if (response.arrayBuffer) {
            arrayBuffer = await response.arrayBuffer()
          } else if (response instanceof ArrayBuffer) {
            arrayBuffer = response
          } else {
            arrayBuffer = new TextEncoder().encode(response.data || response).buffer
          }

          // Convert workbook to CSV
          const workbook = XLSX.read(arrayBuffer, { type: 'array' })
          const firstSheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[firstSheetName]

          // Convert to CSV
          const csvData = XLSX.utils.sheet_to_csv(worksheet, {
            // Optional: you can pass encoding options here if needed
            // FS: field separator
            // RS: row separator
          })

          const utf8WithBom = `\uFEFF${response.data}` // Add BOM for UTF-8
          const blob = new Blob([utf8WithBom], { type: 'text/csv;charset=utf-8;' })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url

          const fileName = `pushed_to_rag_report_${this.params.startDate
            .split('T')[0]
            .replace(/[^a-z0-9]/gi, '_')}-${this.params.endDate
            .split('T')[0]
            .replace(/[^a-z0-9]/gi, '_')}.csv`

          link.href = url
          link.setAttribute('download', fileName)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          // Clean up
          URL.revokeObjectURL(url)
        })
        .catch(error => {
          console.error('Download failed:', error)
        })
        .finally(() => {
          this.loadingDownload = false
        })
    },
    getTopicByCurrentLang(topic) {
      const currentLang = this.$i18n.locale.split('-')[0]
      return topic[currentLang] ? topic[currentLang] : topic.en
    },
    redirectToChatRoom(data) {
      const url = this.$router.resolve({
        name: 'chat',
        query: {
          from: 'topic-summary',
          channelId: data.channel.id,
          msgStartDate: data.logDate,
        },
      })
      window.open(url.href, '_blank')
    },
  },
  setup() {
    const fields = tableFields
    const channelTableFields = channelTableFields
    const searchFields = SearchInputs
    const updateForm = formInput
    const permissionType = 'summary-conversation'
    const route = 'topic'

    return {
      fields,
      searchFields,
      permissionType,
      route,
      updateForm,
      channelTableField,
    }
  },
}
</script>

<style lang="scss">
.lang-input span label {
  display: none !important;
}

.lang-container {
  width: 100%;
  gap: 10px;
}

.btn-filter-bot {
  display: flex;
  flex: 1;
  justify-content: end;
}

.btn-submit-bot-filter {
  height: fit-content;
}

.w-20 {
  width: 100px;
}

.lang-container .v-select.vs--single.vs--searchable {
  width: 150px;
}

.displays {
  display: flex;
  justify-items: center;
}
.btnRetrain {
  border: none;
  background: rgb(6, 172, 67);
  color: rgb(255, 255, 255);
  margin-left: 7px;
  border-radius: 5px;
  cursor: pointer;
}
@import "@core/scss/vue/libs/vue-select.scss";
</style>
